import { useState } from "react";
import { TransactionAccessType } from '../../../../../types/amr-pipeline/enums/TransactionAccessType';
import { filterDocuments, getDealDocLastDate } from "../../../../../utils/amr-pipeline.utils";
import { SearchInput } from "../../../../common";
import { DocumentSection } from "../../../common/documents/DocumentSection";
import { TabBody } from "../../../common/tabs/TabBody";
import { TransactionTabProps } from '../types';
import { withTabTracking } from '../withTabTracking';
import { RequiredFeature } from '../../../../access/RequiredFeature';
import { SubscriptionFeature } from '../../../../../types/billing/SubscriptionFeature';
import { BlockedFeatureContent, SubscribeLink } from "../../../../../components/access/BlockedFeatureText";
import { amrPipelineService } from "../../../../../services/amr-pipeline.service";
import { DocumentType, getDocumentTypeText } from "../../../../../types/amr-pipeline/enums/DocumentType";
import { dealsService } from "../../../../../services/deals.service";
import { user } from "../../../../../user";
import { roles } from "../../../../../constants/roles";
import { compareDates } from "../../../../../utils/compare.utils";

const DocumentsTab = ({ transaction, difference }: TransactionTabProps) => {
    const [searchTerm, setSearchTerm] = useState('');

    if (!transaction.documents) return null

    const isAdminOrDataEntry = user.hasRoles(roles.Administrator, roles.DataEntry);

    const documents = filterDocuments(transaction.documents, searchTerm)
        .sort((a, b) => compareDates(getDealDocLastDate(b), getDealDocLastDate(a)));
    const transactionDocuments = documents.filter(d => d.transactionReferenceName != null);
    const dealDocuments = documents
        .filter(d => d.transactionReferenceName == null && (isAdminOrDataEntry || d.isPublic))
    const distributionReports = dealDocuments.filter(d => d.documentType === DocumentType.distributionReport);
    const monthlyReports = dealDocuments.filter(d => d.documentType === DocumentType.monthlyReport);

    const getEmptyPlaceholder = () => searchTerm.length > 0 ? 'No search results.' : 'There are no documents yet.'

    return (
        <TabBody className="tabs-data-padding component-tabs-deal-documents">
            <RequiredFeature
                feature={SubscriptionFeature.IssuanceMonitorFullAccess}
                renderBlockedContent={() => (
                    <BlockedFeatureContent
                        inline
                        className="empty-placeholder"
                        text={<><SubscribeLink /> to see Documents.</>}
                    />
                )}
            >
                <SearchInput
                    onClear={() => setSearchTerm('')}
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search for documents"
                />
                <DocumentSection
                    title="All Documents"
                    transactionReferenceName={transaction.referenceName}
                    dealReferenceName={transaction.dealReferenceName}
                    emptyPlaceholder={getEmptyPlaceholder()}
                    items={transactionDocuments}
                    difference={difference?.documents}
                    downloadAllFetch={amrPipelineService.getTransactionDocuments}
                    downloadAllArgs={[transaction.dealReferenceName, transaction.referenceName]}
                    fileNameForSave={`${transaction.dealLegalName} Documents.zip`}
                />
                <DocumentSection
                    title="Distribution Reports"
                    dealReferenceName={transaction.dealReferenceName}
                    items={distributionReports}
                    emptyPlaceholder={getEmptyPlaceholder()}
                    downloadAllFetch={dealsService.getDealDocumentsByType}
                    downloadAllArgs={[transaction.dealReferenceName, DocumentType.distributionReport]}
                    fileNameForSave={`${transaction.dealTicker} Distribution Reports.zip`}
                />
                <DocumentSection
                    title="Monthly Reports"
                    dealReferenceName={transaction.dealReferenceName}
                    items={monthlyReports}
                    emptyPlaceholder={getEmptyPlaceholder()}
                    downloadAllFetch={dealsService.getDealDocumentsByType}
                    downloadAllArgs={[transaction.dealReferenceName, DocumentType.monthlyReport]}
                    fileNameForSave={`${transaction.dealTicker} ${getDocumentTypeText(DocumentType.monthlyReport)}.zip`}
                />
            </RequiredFeature>
        </TabBody>
    );
}

export const Documents = withTabTracking(TransactionAccessType.Documents)(DocumentsTab);